// SEO blog content. Casual, accessible tone. Each post renders via BlogPost.jsx.
// Block types: { t: "p" | "h2" | "ul" | "ol" | "quote", text?, items? }

// NOTE: Author/reviewer personas for E-E-A-T bylines. Replace name/bio/credentials
// with your real team members before relying on these for trust signals.
export const AUTHOR = {
  name: "Dr. Elena Harper",
  title: "Cognitive Psychologist, PhD in Psychometrics",
  bio: "Elena researches human reasoning and psychological measurement, and has spent over a decade designing and validating cognitive assessments. She writes to make the science of intelligence clear and useful for everyone.",
  initials: "EH",
};

export const REVIEWER = {
  name: "Dr. Marcus Vale",
  title: "Clinical Psychologist (medical reviewer)",
  initials: "MV",
};

export const BLOG_POSTS = [
  {
    slug: "what-is-a-good-iq-score",
    title: "What Is a Good IQ Score? A Plain-English Guide",
    metaTitle: "What Is a Good IQ Score? Averages, Ranges & Percentiles Explained",
    metaDescription:
      "What counts as a good IQ score? We break down the IQ scale, the average IQ, genius-level scores and what your percentile actually means — in plain English.",
    keywords:
      "what is a good iq score, average iq, iq scale, iq classification, genius iq, iq percentile, high iq, normal iq range",
    excerpt:
      "Is 120 good? Is 100 average? Here's exactly what the numbers mean — and why your percentile matters more than you think.",
    date: "2026-06-01",
    readTime: "6 min read",
    heroImage:
      "https://images.unsplash.com/photo-1586448317606-cb1ec00298fc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    heroAlt: "A line graph on a dark background representing IQ score distribution",
    sections: [
      {
        body: [
          { t: "p", text: "If you've ever finished an IQ test and stared at the number wondering \"wait… is that good?\", you're in good company. IQ scores only make sense once you know what the scale is built on. The good news: it's simpler than it looks." },
        ],
      },
      {
        body: [
          { t: "h2", text: "The average IQ is 100 — on purpose" },
          { t: "p", text: "IQ isn't measured in some absolute unit like centimetres. Instead, scores are designed so that the average person lands at 100. The scale uses a \"standard deviation\" of 15, which is just a fancy way of describing how spread out the scores are. Because of that design, most people cluster near the middle and very few sit at the extremes." },
          { t: "p", text: "In practice, about two-thirds of everyone scores between 85 and 115. So if you land anywhere in that band, you're squarely in the normal range — right where the majority of people are." },
        ],
      },
      {
        body: [
          { t: "h2", text: "The IQ classification scale" },
          { t: "p", text: "Here's the standard way psychologists group IQ scores. Think of these as broad neighbourhoods, not hard walls:" },
          { t: "ul", items: [
            "130 and above — Very Superior (roughly the top 2%, often called the \"gifted\" range)",
            "120–129 — Superior (top 10% of test-takers)",
            "110–119 — High Average (comfortably above typical)",
            "90–109 — Average (where most people land)",
            "Below 90 — Developing (plenty of room to grow with practice)",
          ]},
        ],
      },
      {
        body: [
          { t: "h2", text: "So what's a \"good\" score?" },
          { t: "p", text: "Honestly? Anything above 100 means you scored better than the average person. But if you want a cleaner benchmark: a score of 110+ puts you ahead of roughly 75% of people, and 120+ puts you in the top 10%. A score of 130 or higher is the threshold most high-IQ societies use for membership." },
          { t: "quote", text: "The single most useful number isn't your IQ — it's your percentile. It tells you exactly how many people you scored higher than." },
          { t: "p", text: "That's why The Cognitive Metrics reports your global percentile alongside your score. \"You scored higher than 88% of test-takers\" is a lot more meaningful than a bare number floating in space." },
        ],
      },
      {
        body: [
          { t: "h2", text: "What a good score is NOT" },
          { t: "p", text: "A quick reality check. Your IQ score is a snapshot of certain reasoning skills on a certain day — it is not a verdict on your worth, creativity, work ethic or future success. Some of the most accomplished people on the planet have unremarkable test scores, and plenty of high scorers never do much with it. Treat the number as a starting line, not a finish line." },
        ],
      },
      {
        body: [
          { t: "h2", text: "Find out where you land" },
          { t: "p", text: "The fastest way to answer \"what's a good IQ score for me?\" is to take a properly calibrated test and get your percentile. Our 30-question assessment measures five cognitive domains and gives you an exact score, your global ranking and a breakdown of your strengths — in about 25 minutes." },
        ],
      },
    ],
  },
  {
    slug: "how-to-train-fluid-intelligence",
    title: "How to Train Fluid Intelligence (and Actually See Results)",
    metaTitle: "How to Train Fluid Intelligence — Science-Backed Ways to Boost Reasoning",
    metaDescription:
      "Fluid intelligence is your raw problem-solving horsepower. Here are practical, science-backed ways to train it — and how to measure whether it's working.",
    keywords:
      "how to train fluid intelligence, fluid intelligence, improve reasoning, brain training, working memory, cognitive training, problem solving skills",
    excerpt:
      "Fluid intelligence is the muscle behind solving brand-new problems. Here's how to actually strengthen it — and track your progress.",
    date: "2026-06-03",
    readTime: "7 min read",
    heroImage:
      "https://images.unsplash.com/photo-1605106702734-205df224ecce?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    heroAlt: "A geometric pattern of blue triangles representing abstract reasoning",
    sections: [
      {
        body: [
          { t: "p", text: "Fluid intelligence is the part of your mind that tackles problems you've never seen before — no memorised formula, no prior knowledge, just pure reasoning. It's what an IQ test's pattern and matrix questions are really measuring. And unlike trivia, you can't cram for it. But you can train it." },
        ],
      },
      {
        body: [
          { t: "h2", text: "First, what fluid intelligence actually is" },
          { t: "p", text: "Psychologists split intelligence into two broad types. \"Crystallised\" intelligence is everything you've learned: vocabulary, facts, skills. \"Fluid\" intelligence is your ability to spot patterns, reason abstractly and hold several ideas in your head at once while you work a problem. Fluid intelligence peaks earlier in life and is more sensitive to things like sleep, stress and practice — which is exactly why it's trainable." },
        ],
      },
      {
        body: [
          { t: "h2", text: "What the research points to" },
          { t: "p", text: "There's no magic pill, but a few habits consistently show up in studies on reasoning and working memory:" },
          { t: "ul", items: [
            "Working-memory challenges — exercises like dual n-back push your brain to juggle and update information, the same skill matrix puzzles demand.",
            "Novelty — learning genuinely new things (an instrument, a language, chess) forces your brain to build fresh connections rather than reuse old ones.",
            "Sleep — deep sleep is when your brain consolidates and prunes. Chronically short sleep tanks reasoning performance fast.",
            "Cardio exercise — aerobic activity improves blood flow and is linked to better executive function and processing speed.",
            "Deliberate practice — doing reasoning puzzles with focus and review beats passively scrolling through them.",
          ]},
        ],
      },
      {
        body: [
          { t: "h2", text: "A simple weekly routine" },
          { t: "p", text: "You don't need hours a day. Consistency beats intensity. A realistic plan looks like this:" },
          { t: "ol", items: [
            "15–20 minutes of pattern, matrix or logic puzzles, 4–5 days a week.",
            "One genuinely new skill you're learning on the side (anything that makes you think).",
            "30 minutes of cardio a few times a week.",
            "A hard cap on screens before bed so sleep stays protected.",
          ]},
          { t: "quote", text: "Train the skill the test measures — reasoning under mild time pressure — and the score tends to follow." },
        ],
      },
      {
        body: [
          { t: "h2", text: "How to know it's working" },
          { t: "p", text: "Here's where most people go wrong: they train but never measure, so they can't tell if anything changed. The fix is to benchmark. Take a timed, calibrated assessment now to establish your baseline, train for a few weeks, then retake it and compare your percentile. Watching that line move is both proof and motivation." },
          { t: "p", text: "The Cognitive Metrics makes this easy — every attempt is stored so you can track your percentile over time and see which of the five cognitive domains improved most." },
        ],
      },
    ],
  },
  {
    slug: "how-to-improve-your-iq",
    title: "How to Improve Your IQ: A Realistic, No-Nonsense Guide",
    metaTitle: "How to Improve Your IQ — Realistic, Science-Backed Steps That Work",
    metaDescription:
      "Can you actually raise your IQ? Mostly yes — within limits. Here's a realistic, science-backed plan to improve your reasoning and your test score over time.",
    keywords:
      "how to improve your iq, increase iq, boost iq, raise iq score, brain training, cognitive improvement, get smarter, improve reasoning",
    excerpt:
      "Can you really raise your IQ? Here's the honest answer — plus a practical plan that moves the needle on your score.",
    date: "2026-06-05",
    readTime: "7 min read",
    heroImage:
      "https://images.unsplash.com/photo-1513258496099-48168024aec0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    heroAlt: "A focused person thinking in front of a laptop",
    sections: [
      {
        body: [
          { t: "p", text: "Let's start with the honest version: you probably can't turn a score of 100 into 160. But \"your IQ is fixed forever\" is also a myth. Reasoning ability responds to practice, lifestyle and how well you actually perform on test day. For most people there's real, measurable room to improve — and that's what this guide is about." },
        ],
      },
      {
        body: [
          { t: "h2", text: "Why improvement is possible" },
          { t: "p", text: "Your brain is plastic — it rewires itself based on what you repeatedly ask it to do. Two things drive a higher score. The first is genuine skill: better pattern recognition, faster working memory, sharper logic. The second is \"test readiness\": being rested, calm and familiar with the question formats so your real ability actually shows up instead of getting buried under nerves and fatigue." },
        ],
      },
      {
        body: [
          { t: "h2", text: "The plan that actually moves the needle" },
          { t: "ol", items: [
            "Benchmark first. Take a calibrated test and record your score and percentile. You can't improve what you never measured.",
            "Find your weak domain. A good report breaks your score into areas — spatial, logical, numerical, verbal, fluid. Train the weakest one; that's where points are cheapest to earn.",
            "Practice the formats. Matrices, number series and verbal analogies all have recurring logic. Familiarity removes the \"what am I even looking at\" tax.",
            "Protect sleep and stress. A tired, anxious brain can lose the equivalent of several points. Rest is a performance strategy, not a luxury.",
            "Retake and compare. After a few weeks, test again and watch the percentile move.",
          ]},
        ],
      },
      {
        body: [
          { t: "h2", text: "Habits that compound" },
          { t: "ul", items: [
            "Read things slightly above your comfort level — it builds verbal reasoning quietly.",
            "Play strategy and logic games that force planning several steps ahead.",
            "Learn something structured and new (a language, coding, music) to keep building fresh neural pathways.",
            "Move your body; cardio is one of the best-supported cognitive enhancers we have.",
          ]},
          { t: "quote", text: "Measure, train your weakest area, rest properly, retake. That loop is boring — and it works." },
        ],
      },
      {
        body: [
          { t: "h2", text: "Set your baseline today" },
          { t: "p", text: "Improvement without measurement is just a feeling. Take the 30-question assessment, get your exact score and five-domain breakdown, and you'll know precisely where to aim. Then come back in a few weeks and prove to yourself that the work paid off." },
        ],
      },
    ],
  },
  {
    slug: "average-iq-by-age-explained",
    title: "Average IQ by Age, Explained",
    metaTitle: "Average IQ by Age — How Scores Change Across the Lifespan",
    metaDescription:
      "Does IQ change with age? Here's how average IQ scores are adjusted for age, what rises and falls over a lifetime, and why your percentile is what really counts.",
    keywords:
      "average iq by age, iq by age, does iq change with age, iq and aging, average iq, iq norms, iq percentile by age",
    excerpt:
      "Does your IQ really change as you get older? The answer is more interesting — and more reassuring — than you'd expect.",
    date: "2026-06-07",
    readTime: "6 min read",
    heroImage:
      "https://images.unsplash.com/photo-1622352496174-9e1d969b1945?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    heroAlt: "A diverse group of people of different ages together",
    sections: [
      {
        body: [
          { t: "p", text: "\"What's the average IQ for my age?\" is one of the most common questions people ask — and the answer surprises most of them. Because of how IQ tests are built, the average is 100 at every age. Let's unpack why, and what actually changes under the hood as you get older." },
        ],
      },
      {
        body: [
          { t: "h2", text: "Why the average is always 100" },
          { t: "p", text: "IQ scores are age-adjusted. When a test is calibrated, your raw performance is compared to other people in your age group, then mapped onto a scale where that group's average is set to 100. So a sharp 70-year-old and a sharp 25-year-old can both score 130 — each is being compared to their own peers, not to each other. The number is always relative." },
        ],
      },
      {
        body: [
          { t: "h2", text: "What actually changes with age" },
          { t: "p", text: "Even though the average stays pinned at 100, the raw abilities underneath shift over a lifetime — and in opposite directions:" },
          { t: "ul", items: [
            "Fluid intelligence (raw problem-solving and processing speed) tends to peak in the late teens to late twenties, then gradually eases.",
            "Crystallised intelligence (vocabulary, knowledge, judgement) keeps climbing well into middle age and often holds steady far longer.",
            "This is why older adults often reason more wisely even as younger adults solve novel puzzles faster — different strengths, different seasons.",
          ]},
        ],
      },
      {
        body: [
          { t: "h2", text: "The Flynn effect: a twist across generations" },
          { t: "p", text: "There's a famous wrinkle called the Flynn effect — average raw scores rose substantially across the 20th century, generation after generation, likely due to better nutrition, education and more abstract thinking in everyday life. Tests get re-standardised periodically to keep the average at 100, which is partly why comparing scores across decades is tricky." },
          { t: "quote", text: "Your age doesn't change what a \"good\" score is — 100 is average whether you're 18 or 80. What matters is your percentile among your peers." },
        ],
      },
      {
        body: [
          { t: "h2", text: "The bottom line" },
          { t: "p", text: "Don't get hung up on finding a different \"target number\" for your age. Focus on your percentile — the share of people you scored higher than — and on keeping your mind active, because staying mentally engaged is strongly linked to protecting cognitive sharpness as you age." },
          { t: "p", text: "Curious where you land right now? Take the assessment, get your percentile, and come back any time to see how you're tracking." },
        ],
      },
    ],
  },
  {
    slug: "iq-vs-eq",
    title: "IQ vs EQ: What's the Difference (and Which Matters More)?",
    metaTitle: "IQ vs EQ — What's the Difference and Which Matters More?",
    metaDescription:
      "IQ vs EQ explained in plain English: what each one measures, how they differ, whether they're linked, and which actually matters more for success and happiness.",
    keywords:
      "iq vs eq, difference between iq and eq, emotional intelligence, iq and emotional intelligence, eq vs iq, what is eq, iq or eq more important",
    excerpt:
      "One measures reasoning, the other measures emotion. Here's how IQ and EQ really differ — and which one matters more.",
    date: "2026-06-09",
    readTime: "7 min read",
    heroImage:
      "https://images.unsplash.com/photo-1721734081214-1be31adce713?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    heroAlt: "A metallic human head profile with a glowing, colourful brain inside",
    sections: [
      {
        body: [
          { t: "p", text: "You've probably heard someone say \"sure, they're smart, but they have no EQ.\" IQ and EQ get tossed around as rivals, but they actually measure very different things — and understanding the difference changes how you think about your own strengths." },
        ],
      },
      {
        body: [
          { t: "h2", text: "What IQ measures" },
          { t: "p", text: "IQ — intelligence quotient — is a score for cognitive ability: reasoning, pattern recognition, working memory, processing speed and problem-solving. It's what a classic IQ test captures through matrices, number series and logical puzzles. IQ is relatively stable over time and is a strong predictor of how quickly you learn complex material." },
        ],
      },
      {
        body: [
          { t: "h2", text: "What EQ measures" },
          { t: "p", text: "EQ — emotional quotient, or emotional intelligence — is about recognising, understanding and managing emotions: your own and other people's. It shows up in self-awareness, empathy, impulse control and social skill. Unlike IQ, EQ tends to be more learnable and often improves with life experience and deliberate practice." },
        ],
      },
      {
        body: [
          { t: "h2", text: "The key differences at a glance" },
          { t: "ul", items: [
            "Focus: IQ = thinking and reasoning; EQ = feeling and relating.",
            "Stability: IQ is fairly fixed; EQ is more trainable over a lifetime.",
            "Measurement: IQ is measured with standardised, objectively-scored tests; EQ relies more on self-report and behavioural assessment.",
            "Predicts: IQ predicts academic and technical performance; EQ predicts leadership, teamwork and relationship quality.",
          ]},
        ],
      },
      {
        body: [
          { t: "h2", text: "Are they connected?" },
          { t: "p", text: "Mostly they're independent — a high IQ doesn't guarantee high EQ, and vice versa. The two can absolutely coexist, and the most effective people often pair solid reasoning with strong emotional awareness. Thinking of them as a rivalry misses the point: they're different tools for different jobs." },
          { t: "quote", text: "IQ may get you in the door; EQ often determines how far you go once you're inside." },
        ],
      },
      {
        body: [
          { t: "h2", text: "So which matters more?" },
          { t: "p", text: "It depends on the goal. For mastering technical or analytical work, IQ carries more weight. For leading teams, navigating conflict and building relationships, EQ frequently matters more. For a fulfilling life overall, you want a healthy dose of both — plus persistence, which neither score measures." },
        ],
      },
      {
        body: [
          { t: "h2", text: "Start with what you can measure" },
          { t: "p", text: "EQ is slippery to quantify, but your IQ isn't. Getting a clear, calibrated read on your reasoning ability is a concrete first step in understanding your cognitive profile. Take the 30-question assessment to see your exact score, percentile and a breakdown across five reasoning domains." },
        ],
      },
    ],
  },
  {
    slug: "what-iq-is-considered-genius",
    title: "What IQ Is Considered Genius?",
    metaTitle: "What IQ Is Considered Genius? The Numbers, Myths & Reality",
    metaDescription:
      "What IQ is considered genius? We break down the genius IQ threshold, how rare it is, famous estimated IQs, and why genius is about far more than a number.",
    keywords:
      "what iq is considered genius, genius iq, genius iq score, highest iq, iq 140, iq 160, einstein iq, how rare is genius iq",
    excerpt:
      "Is it 140? 160? Here's the real threshold for a 'genius' IQ, how rare it is, and why the number tells only part of the story.",
    date: "2026-06-11",
    readTime: "6 min read",
    heroImage:
      "https://images.unsplash.com/photo-1635372722656-389f87a941b7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    heroAlt: "A dark chalkboard densely covered in complex handwritten equations",
    sections: [
      {
        body: [
          { t: "p", text: "\"Genius\" is one of the most loaded words in the IQ world — and one of the least precisely defined. There's no single official cutoff, but there are widely-used thresholds and some fascinating context. Let's separate the numbers from the myths." },
        ],
      },
      {
        body: [
          { t: "h2", text: "The usual genius threshold" },
          { t: "p", text: "Most sources place the \"genius or near-genius\" range at an IQ of 140 and above. Some stricter definitions push it to 145 or even 160. For perspective, a score of 140 is already extraordinarily rare — fewer than 1 in 250 people reach it. By the time you're talking about 160+, you're looking at roughly 1 in 30,000." },
        ],
      },
      {
        body: [
          { t: "h2", text: "How the rarity breaks down" },
          { t: "ul", items: [
            "130+ — \"gifted\" range, about the top 2% of people.",
            "140+ — often labelled near-genius, well under 1% of the population.",
            "160+ — exceptionally rare, around 1 in 30,000.",
            "180+ — so rare it's hard to measure reliably; most standard tests don't even extend this far.",
          ]},
        ],
      },
      {
        body: [
          { t: "h2", text: "What about famous 'geniuses'?" },
          { t: "p", text: "You'll often see eye-popping numbers attached to historical figures — Einstein estimated around 160, and so on. It's worth knowing these are almost always retrospective estimates, not real test results. Many of history's greatest minds were never formally tested at all. The figures make for fun trivia, but they aren't hard data." },
          { t: "quote", text: "A high score describes potential, not achievement. Genius is what you do with the mind you have." },
        ],
      },
      {
        body: [
          { t: "h2", text: "Why the number isn't the whole story" },
          { t: "p", text: "Real-world genius almost always combines raw ability with relentless curiosity, deep practice, creativity and the courage to pursue hard problems for years. Plenty of people with towering IQs never produce anything remarkable, while others with more modest scores change the world through persistence and originality. The test measures one ingredient — not the whole recipe." },
        ],
      },
      {
        body: [
          { t: "h2", text: "Curious where you stand?" },
          { t: "p", text: "Wondering whether you're near that gifted or genius range? Take the credited 30-question assessment to get your exact IQ score and global percentile — then decide what you want to build with it." },
        ],
      },
    ],
  },
];

export const getPost = (slug) => BLOG_POSTS.find((p) => p.slug === slug);
